import { HttpInterceptorFn, HttpRequest, HttpHandlerFn, HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { catchError, throwError } from 'rxjs';
import { Router } from '@angular/router';
import { SessionService } from '../services/session/session.service';
import { ErrorNotificationService } from '../services/error-notification.service'; // Import the new service

export const errorInterceptor: HttpInterceptorFn = (req: HttpRequest<unknown>, next: HttpHandlerFn) => {
  // Inject the new error notification service
  const errorNotificationService = inject(ErrorNotificationService);
  const router = inject(Router);
  const sessionService = inject(SessionService);

  return next(req).pipe(
    catchError((error: HttpErrorResponse) => {
      let errorMessage = 'An unknown error occurred!';
      let errorDetail = 'Please try again later.';

      if (error.error instanceof ErrorEvent) {
        // Client-side error
        errorMessage = `Client Error: ${error.error.message}`;
      } else {
        // Server-side error
        switch (error.status) {
          case 0: // Potential CORS or Network Error
            errorMessage = 'Network Error';
            errorDetail = 'Could not connect to the server. Please check your network connection or contact support.';
            break;
          case 400: // Bad Request
            errorMessage = 'Bad Request';
            errorDetail = error.error?.message || 'The request was invalid.';
            break;
          case 401: // Unauthorized
            errorMessage = 'Unauthorized';
            errorDetail = error.error?.message || 'You are not authorized to access this resource. Please log in again.';
            // If the 401 came from the login endpoint, do NOT force logout/navigation so the client can show the server message.
            if (!req.url?.includes('/auth/login')) {
              sessionService.logout(true);
              localStorage.clear();
              router.navigate(['/login']);
            }
            break;
          case 403: // Forbidden
            errorMessage = 'Forbidden';
            if (
              error.headers?.get('content-type')?.includes('text/html') ||
              (typeof error.error === 'string' && /<html[\s>]/i.test(error.error))
            ) {
              errorDetail = 'We couldn\'t process your request for safety reasons. Please contact our support team for further information.';
            } else {
              errorDetail = error.error?.message || 'You do not have permission to perform this action.';
            }
            break;
          case 404: // Not Found
            errorMessage = 'Not Found';
            errorDetail = error.error?.message || 'The requested resource could not be found.';
            break;
          case 422: // Unprocessable Entity
            errorMessage = 'Validation Error';
            if (error.error && Array.isArray(error.error.detail)) {
              errorDetail = error.error.detail.map((err: any) => `${err.loc.join('.')} - ${err.msg}`).join('; ');
            } else {
              errorDetail = error.error?.message || 'Invalid data provided.';
            }
            break;
          case 500: // Internal Server Error
            errorMessage = 'Server Error';
            errorDetail = error.error?.message || 'Something went wrong on the server. Please try again later.';
            break;
          default:
            errorMessage = `HTTP Error: ${error.status}`;
            errorDetail = error.error?.message || error.statusText;
            break;
        }
      }

      // Use the service to show the error. It will handle the debouncing.
      // For 401 from login endpoint we want to display the toast; otherwise skip toast since redirect/logout is handled.
      const isLogin401 = error.status === 401 && req.url?.includes('/auth/login');
      if (isLogin401 || error.status !== 401) {
        errorNotificationService.showError(errorMessage, errorDetail);
      }

      // Re-throw the error to propagate it
      return throwError(() => new Error(errorDetail));
    })
  );
};
